import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

export interface PluginErrorBoundaryProps {
  /** Notifica `usePluginLoadRetry` (HUB-237, CA-06/07/08) — a decisão de
   * retentar ou finalizar com erro vive inteiramente no hook, não aqui. */
  onError: (error: unknown) => void;
  children: ReactNode;
}

interface PluginErrorBoundaryState {
  hasError: boolean;
}

/**
 * Único mecanismo capaz de capturar um erro de render do plugin já montado
 * (HUB-237, CA-05/CA-06) — `try/catch` não alcança erros de fase de render
 * do React, só um Error Boundary de classe. Renderiza `null` quando captura
 * um erro (a página segue responsiva — o passo pai, dirigido por
 * `usePluginLoadRetry`, assume a tela de retry/erro final).
 *
 * Deve ser remontado com `key` diferente a cada nova tentativa (ver
 * `mountKey` em `usePluginLoadRetry`) — um Error Boundary não reseta seu
 * próprio `hasError` sozinho, e a spec exige recarregar o módulo do zero, não
 * apenas remontar o componente que acabou de falhar.
 */
export class PluginErrorBoundary extends Component<PluginErrorBoundaryProps, PluginErrorBoundaryState> {
  override state: PluginErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PluginErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, _info: ErrorInfo): void {
    this.props.onError(error);
  }

  override render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
